import { Injectable } from '@angular/core';
import { Observable, of, throwError, from, forkJoin } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Budget, BudgetProduct, BudgetClient, BudgetVehicle, BudgetProductDetailDTO, BudgetDetailsDTO, BudgetDTO, AvailableProductItemResponseDTO, AvailableProductItemDTO, BudgetEmailPayload, ApproveBudgetBody, BudgetGenericProductItem } from '../interfaces/budget.interface';
import { HttpClient, HttpContext, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { SKIP_DEDUPE } from 'src/app/core/interceptor/inflight-dedupe.interceptor';
import { environment } from '../../../../environments/environment';
import { AuthService } from 'src/app/auth/auth.service';
import { UserRoleService } from 'src/app/core/services/role.service';
import { ClientsService } from '../../clients/services/clients.service';
import { ClientDto, ClientType, ClientTypeApi, ClientTypeInput } from 'src/app/models/client.model';
import { VehicleDto } from 'src/app/models/vehicle.model';
import { VehiclesService } from '../../vehicles/services/vehicles.service';
import { SnackbarService } from 'src/app/shared/snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsEvent, ResolutionType, SendChannelType } from 'src/app/analytics/models/analytics-event';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { BudgetTireProductsResponse } from '../interfaces/budget.interface';
import { ProductFilters } from 'src/app/core/interfaces/product.interface';
import { languageToLocaleEmail } from 'src/app/core/utils/country.utils';



@Injectable({
  providedIn: 'root'
})
export class BudgetService {
  private readonly apiUrl = environment.cafler_backoffice_back_url;
  private static readonly CLIENT_TYPE_COMPANY_NUMERIC_LITERAL = '2';
  private static readonly CLIENT_TYPE_INDIVIDUAL_NUMERIC_LITERAL = '1';
  private static readonly CLIENT_TYPE_COMPANY_ALIASES: ReadonlySet<string> = new Set(['b2b', 'company', 'business']);
  private static readonly CLIENT_TYPE_INDIVIDUAL_ALIASES: ReadonlySet<string> = new Set(['b2c', 'individual', 'consumer']);

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly userRoleService: UserRoleService,
    private readonly clientService: ClientsService,
    private readonly vehiclesService: VehiclesService,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
    private readonly analytics: AnalyticsService
  ) {
  }

  getAvailableProductsBudgetsRequest(filters?: ProductFilters, page: number = 1, size: number = 15, continuationToken?: string): Observable<BudgetTireProductsResponse> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) throw new Error('No token available');

        let params = new HttpParams({
          fromObject: {
            pageNumber: page.toString(),
            pageSize: size.toString(),
            ...(continuationToken && { continuationToken }),
            ...(filters?.searchText && { searchText: filters.searchText }),
            ...(filters?.qualityId && { qualityId: filters.qualityId }),
            ...(filters?.brandIds?.length && { brandIds: filters.brandIds }),
            ...(filters?.brandNames?.length && { brandNames: filters.brandNames }),
            ...(filters?.season && { season: filters.season.toString() }),
            ...(filters?.width && { width: filters.width.toString() }),
            ...(filters?.runflat != null && { runflat: filters.runflat.toString() }),
            ...(filters?.sortBy && { sortBy: filters.sortBy.toString() }),
            ...(filters?.sortOrder && { sortOrder: filters.sortOrder.toString() }),
          }
        });

        const context = new HttpContext().set(SKIP_DEDUPE, true);

        return this.http.get<BudgetTireProductsResponse>(`${this.apiUrl}/tire-products/budget/paginated`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: params,
          context
        })

      })
    );
  }


  getBudgetById(id: number): Observable<Budget | undefined> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.get<BudgetDetailsDTO>(`${this.apiUrl}/Budgets/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }).pipe(
          map(budgetDTO => this.mapBudgetDetailsDTO_to_Budget(budgetDTO))
        );
      }),
      catchError(this.handleError)
    );
  }

  createBudget(budget: Budget): Observable<Budget> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No token available');
        }

        return this.userRoleService.getCurrentUser().pipe(
          switchMap(user => {
            const centerId = this.resolveCenterId(budget, user?.centerId);
            const budgetDTO = this.mapBudget_to_BudgetDTO(budget, centerId);

            return this.http.post<BudgetDetailsDTO>(`${this.apiUrl}/Budgets`, budgetDTO, {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }).pipe(
              tap((budgetDTO: BudgetDetailsDTO) => {
                this.analytics.track(AnalyticsEvent.BudgetSaveSuccess, {
                  ...this.analytics.globalProps(),
                  budget_id: budgetDTO.id,
                  items_count: this.getProductItemsAmout(budget),
                  total_amount: budgetDTO.total,
                });
              }),
              map(budget => this.mapBudgetDetailsDTO_to_Budget(budget))
            );
          })
        );
      }),
      catchError((err: HttpErrorResponse) => {
        this.snackbarService.showError(
          this.translate.instant('budget.show_error_message.save_budget')
        )

        const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
        const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

        this.analytics.track(AnalyticsEvent.BudgetSaveFail, {
          ...this.analytics.globalProps(),
          budget_id: budget.id ?? 0,
          error_code: errorCode,
          http_status: err.status,
          error_message: errorMessage,
        });
        return throwError(() => err);
      })
    );
  }

  updateBudget(budgetId: number, budget: Budget): Observable<Budget> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No token available');
        }

        return this.userRoleService.getCurrentUser().pipe(
          switchMap(user => {
            const centerId = this.resolveCenterId(budget, user?.centerId);
            const budgetDTO = this.mapBudget_to_BudgetDTO(budget, centerId);

            return this.http.put<BudgetDetailsDTO>(`${this.apiUrl}/Budgets/${budgetId}`, budgetDTO, {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }).pipe(
              tap((budgetDTO: BudgetDetailsDTO) => {
                this.analytics.track(AnalyticsEvent.BudgetSaveSuccess, {
                  ...this.analytics.globalProps(),
                  budget_id: budgetDTO.id,
                  items_count: this.getProductItemsAmout(budget),
                  total_amount: budgetDTO.total,
                });
              }),
              map(budget => this.mapBudgetDetailsDTO_to_Budget(budget))
            );
          })
        );
      }),
      catchError((err: HttpErrorResponse) => {
        this.snackbarService.showError(
          this.translate.instant('budget.show_error_message.save_budget')
        )
        const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
        const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

        this.analytics.track(AnalyticsEvent.BudgetSaveFail, {
          ...this.analytics.globalProps(),
          budget_id: budget.id ?? 0,
          error_code: errorCode,
          http_status: err.status,
          error_message: errorMessage,
        });
        return throwError(() => err);
      })
    );
  }


  /**
   * Propaga el HttpErrorResponse original (no lo envuelve en `new Error`) para que
   * los consumidores puedan inspeccionar status, headers y payload en `catchError`.
   * El log se mantiene en consola pero sin exponer cuerpo completo (evita filtrar
   * datos sensibles del backend en producción).
   */
  private handleError = (error: HttpErrorResponse) => {
    console.error(`API Error [${error?.status ?? '?'}] ${error?.url ?? ''}`);
    return throwError(() => error);
  };

  showPricesError(error?: HttpErrorResponse) {
    // Acceso defensivo: error.error puede ser ErrorEvent (red) o un payload arbitrario.
    const backendPayload = error?.error;
    const messageFromBackend = (typeof backendPayload === 'object' && backendPayload !== null
      && typeof (backendPayload as { message?: unknown }).message === 'string')
      ? (backendPayload as { message: string }).message
      : null;
    const errorKey = messageFromBackend ?? 'budget.error_price_products_description';
    this.snackbarService.showError(this.translate.instant(errorKey));
    // Propagamos el HttpErrorResponse original (no `new Error(error?.error)` que
    // serializaba a "[object Object]") para que el caller pueda hacer narrowing.
    return throwError(() => error ?? new Error('Unknown pricing error'));
  }



  sendBudgetEmail(budgetId: number, valid_days_amount: string, pdfBase64: string) {
    const currLanguage = this.translate.currentLang
    const locale = languageToLocaleEmail(currLanguage);

    const payload: BudgetEmailPayload = {
      EndValidityDate: valid_days_amount,
      Locale: locale, // Aquí podrías mapear el locale actual a SupportedLocale si es necesario
      PdfBase64: pdfBase64,
    }

    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }

        return this.http.post<BudgetDetailsDTO>(`${this.apiUrl}/Budgets/${budgetId}/sent`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }),
      tap((resp: BudgetDetailsDTO) => {
        this.analytics.track(AnalyticsEvent.BudgetSendSuccess, {
          ...this.analytics.globalProps(),
          budget_id: resp.id,
          send_channel: SendChannelType.Email,
        });
      }),
      catchError((err: HttpErrorResponse) => {

        this.snackbarService.showError(
          this.translate.instant('budget.show_error_message.sending_email')
        )

        const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
        const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

        this.analytics.track(AnalyticsEvent.BudgetSendFail, {
          ...this.analytics.globalProps(),
          budget_id: budgetId,
          error_code: errorCode,
          errorMessage: errorMessage,
          http_status: err.status,
          send_channel: SendChannelType.Email
        });

        return throwError(() => err);
      })
    );
  }


  /**
   * Approves a budget by sending the required data to the backend.
   * @param budgetId The ID of the budget to approve.
   * @param body The approval details (serviceTypeId, agendaDate, agendaTime).
   */
  approveBudget(budgetId: number, body: ApproveBudgetBody): Observable<any> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No token available');
        }

        return this.http.patch(`${this.apiUrl}/Budgets/${budgetId}/accept`, body, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }),
      tap((resp: any) => {
        this.analytics.track(AnalyticsEvent.BudgetResolvedSuccess, {
          ...this.analytics.globalProps(),
          budget_id: budgetId,
          resolution: ResolutionType.Accepted,
        });
      }),
      catchError((err: HttpErrorResponse) => {
        const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
        const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

        this.analytics.track(AnalyticsEvent.BudgetResolvedFail, {
          ...this.analytics.globalProps(),
          budget_id: budgetId,
          resolution: ResolutionType.Accepted,
          error_code: errorCode,
          error_message: errorMessage,
          http_status: err.status
        });

        return throwError(() => err);
      })
    );
  }

  rejectBudget(budgetId: number, rejectionReason?: string): Observable<any> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No token available');
        }

        const payload = {
          reason: rejectionReason || ''
        };

        return this.http.post(`${this.apiUrl}/Budgets/${budgetId}/rejected`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }),

      tap((resp: any) => {
        this.analytics.track(AnalyticsEvent.BudgetResolvedSuccess, {
          ...this.analytics.globalProps(),
          budget_id: budgetId,
          resolution: ResolutionType.Rejected,
          reason: rejectionReason
        });
      }),

      catchError((err: HttpErrorResponse) => {
        const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
        const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

        this.analytics.track(AnalyticsEvent.BudgetResolvedFail, {
          ...this.analytics.globalProps(),
          budget_id: budgetId,
          resolution: ResolutionType.Rejected,
          reason: rejectionReason,
          error_code: errorCode,
          error_message: errorMessage,
          http_status: err.status
        });

        return throwError(() => err);
      })
    );
  }

  // UTILS

  async getBudgetWithClientAndVehicle(budget: Budget): Promise<Budget> {
    const client = await this.getClient(budget)
    const vehicle = await this.getVehicle(budget)

    return {
      ...budget,
      client,
      vehicle
    }
  }

  async getClient(budget: Budget): Promise<BudgetClient | undefined> {
    let client: BudgetClient | undefined;
    if (budget.clientId) {
      let clientDto: ClientDto | undefined = await this.clientService.getClientById(budget.clientId) ?? undefined;
      client = clientDto ? this.mapClientDto_to_Client(clientDto) : undefined
    } else if (budget.client) {
      client = budget.client
    } else {
      client = undefined
    }

    return client;
  }

  async getVehicle(budget: Budget): Promise<BudgetVehicle | undefined> {
    let vehicle: BudgetVehicle | undefined;

    if (!budget.vehicleId || !budget.clientId) {
      return
    }
    const vehicles: VehicleDto[] = await this.vehiclesService.searchVehiclesByClientId(budget.clientId)
    if (vehicles) {
      const vehicleDto: VehicleDto | undefined = vehicles?.find(x => x.id == budget.vehicleId);
      vehicle = vehicleDto ? this.mapVehicleDto_to_Vehicle(vehicleDto) : undefined
    } else {
      vehicle = undefined;
    }

    return vehicle;
  }

  getProductItemsAmout(budget: Budget): number {
    const productsAmount =
      budget?.budgetProducts?.reduce((total, product) => {
        return total + (product?.quantity ?? 0);
      }, 0) ?? 0;

    const itemsAmount =
      budget?.budgetItems?.reduce((total, item) => {
        return total + (item?.quantity ?? 0);
      }, 0) ?? 0;

    return productsAmount + itemsAmount;
  }

  calculateAvailableProductsPrices(AvailableProductArray: BudgetProduct[]): Observable<BudgetProduct[]> {
    const products = AvailableProductArray.map((x: BudgetProduct): AvailableProductItemDTO => {
      return {
        productId: x.productId == 0 ? null : x.productId,
        identifier: x.identifier,
        productCost: x.cost ?? 0,
      }
    })

    return from(this.authService.getAccessToken()).pipe(
      switchMap(token => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.post<AvailableProductItemResponseDTO[]>(`${this.apiUrl}/tire-products/prices`, products, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).pipe(
          map((newItems: AvailableProductItemResponseDTO[]) =>
            this.updateProductsFromResponse(AvailableProductArray, newItems))
        );
      }),
      catchError(err => this.showPricesError(err))
    );
  }

  updateProductsFromResponse(
    originalItems: BudgetProduct[],
    responseItems: AvailableProductItemResponseDTO[]
  ): BudgetProduct[] {

    // Convertir el array original en un mapa por EAN -> index
    const originalMap = new Map<string, number>();

    originalItems.forEach((item, index) => {
      if (item.identifier) {
        originalMap.set(item.identifier, index);
      }
    });

    // Recorrer la respuesta del backend
    responseItems.forEach(response => {
      if (!response.identifier) return;

      const index = originalMap.get(response.identifier);
      if (index === undefined) return;

      const original = originalItems[index];

      // Actualizar productId y price
      original.productId = response.productId ?? original.productId;
      original.price = response.productPrice;  // o usa "price" si tienes otra propiedad
    });

    return originalItems;
  }

  // MAPPERS



  private mapClientDto_to_Client(clientDto: ClientDto): BudgetClient {
    return {
      id: clientDto.id,
      fullName: String(clientDto.fullName ?? clientDto.clientFullName ?? clientDto.email ?? `Cliente #${clientDto.id}`).trim(),
      email: clientDto.email,
      phoneNumber: clientDto.phoneNumber,
      documentType: clientDto.documentType,
      documentId: clientDto.documentId,
      address: clientDto.address,
      clientType: this.normalizeClientTypeToApi(clientDto.clientType),
      centerId: clientDto.centerId
    };
  }

  private mapVehicleDto_to_Vehicle(vehicleDto: VehicleDto): BudgetVehicle {
    return {
      ...vehicleDto,
      modificationDate: vehicleDto.modificationDate || null
    };
  }

  private mapBudget_to_BudgetDTO(budget: Budget, centerId: number): BudgetDTO {
    return {
      id: budget.id,
      centerId,
      subtotal: budget.subTotalAmount ?? 0,
      total: budget.total ?? 0,
      profit: budget.profit ?? 0,
      discount: budget.discount,
      cost: budget.cost,
      vatAmount: budget.VATAmount ?? 0,
      vatPercentage: budget.VATPercentage ?? 0,
      budgetItems: budget.budgetItems,
      budgetProducts: budget.budgetProducts,
      budgetServices: budget.budgetServices,
      BudgetCustomItems: budget.budgetCustomItems,
      budgetNotes: budget.budgetNotes,
      existingClientId: budget.clientId ?? null,
      existingVehicleId: budget.vehicleId ?? null,
      clientFullName: budget.client?.fullName ?? null,
      clientPhoneNumber: budget.client?.phoneNumber ?? null,
      clientEmail: budget.client?.email ?? null,
      clientType: this.normalizeClientTypeToApi(budget.client?.clientType),
    }
  }

  private resolveCenterId(budget: Budget, fallbackCenterId?: number | null): number {
    const toValidCenterId = (value: unknown): number | null => {
      if (value === null || value === undefined) return null;
      const normalized = typeof value === 'string' ? Number(value.trim()) : Number(value);
      if (!Number.isInteger(normalized) || normalized <= 0) return null;
      return normalized;
    };
    const candidates = [budget.centerId, budget.client?.centerId, fallbackCenterId];
    const valid = candidates.map(toValidCenterId).find((value): value is number => value !== null);

    if (!valid) {
      throw new Error('Budget centerId is required');
    }

    return valid;
  }

  private mapBudgetDetailsDTO_to_Budget(budgetDTO: BudgetDetailsDTO): Budget {
    return {
      ...budgetDTO,
      client: budgetDTO.client ? {
        id: budgetDTO.client.id,
        fullName: budgetDTO.client.fullName,
        email: budgetDTO.client.email,
        phoneNumber: budgetDTO.client.phoneNumber,
        lastVisit: budgetDTO.client.lastVisit ? new Date(budgetDTO.client.lastVisit) : null,
        documentType: budgetDTO.client.documentType,
        documentId: budgetDTO.client.documentId,
        address: budgetDTO.client.address,
        clientType: this.normalizeClientTypeToApi(budgetDTO.client.clientType),
        centerId: budgetDTO.client.centerId
      } : budgetDTO.clientFullName ? {
        fullName: budgetDTO.clientFullName,
        email: budgetDTO.clientEmail,
        phoneNumber: budgetDTO.clientPhoneNumber,
        clientType: this.normalizeClientTypeToApi(budgetDTO.clientType),
      } : undefined,
      clientId: budgetDTO.client?.id ?? undefined,
      vehicleId: budgetDTO.vehicle?.id ?? undefined,

      VATAmount: budgetDTO.vatAmount,
      VATPercentage: budgetDTO.vatPercentage,

      budgetProducts: budgetDTO.budgetProducts
    }
  }

  private normalizeClientTypeToApi(value: ClientTypeInput | ClientTypeApi | undefined): ClientTypeApi {
    if (value === ClientTypeApi.Company || value === BudgetService.CLIENT_TYPE_COMPANY_NUMERIC_LITERAL) {
      return ClientTypeApi.Company;
    }

    if (value === ClientTypeApi.Individual || value === BudgetService.CLIENT_TYPE_INDIVIDUAL_NUMERIC_LITERAL || value === null || value === undefined) {
      return ClientTypeApi.Individual;
    }

    const normalized = String(value).trim().toLowerCase();
    if (BudgetService.CLIENT_TYPE_COMPANY_ALIASES.has(normalized)) {
      return ClientTypeApi.Company;
    }

    if (BudgetService.CLIENT_TYPE_INDIVIDUAL_ALIASES.has(normalized)) {
      return ClientTypeApi.Individual;
    }

    if (value === ClientType.B2B) {
      return ClientTypeApi.Company;
    }

    return ClientTypeApi.Individual;
  }



} 
